import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Title,
  Text,
  Stack,
  Group,
  Badge,
  Loader,
  Center,
  SimpleGrid,
  Paper,
  Divider,
  Button,
  TextInput,
  TagsInput,
  Modal,
  ActionIcon,
  Select,
  Accordion,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { categoriesApi } from '../api/categories';
import { pageContainer } from '../styles/theme.css';
import type { CategoriesData, CategoryEntry, ColorEntry } from '../types';

// ─── Shared hook ──────────────────────────────────────────────────────────────

function useCategories() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.get,
    staleTime: Infinity,
  });
  const saveMutation = useMutation({
    mutationFn: (updated: CategoriesData) => categoriesApi.update(updated),
    onSuccess: (updated) => {
      qc.setQueryData(['categories'], updated);
      notifications.show({ message: 'Gemt', color: 'green' });
    },
    onError: (err) => {
      notifications.show({ message: (err as Error).message, color: 'red' });
    },
  });
  return { data, isLoading, save: saveMutation.mutate, isSaving: saveMutation.isPending };
}

// ─── Generic card ─────────────────────────────────────────────────────────────

function MetaCard({
  title,
  subtitle,
  tags,
  onEdit,
  onDelete,
  isSaving,
}: {
  title: string;
  subtitle?: string;
  tags?: string[];
  onEdit?: () => void;
  onDelete: () => void;
  isSaving?: boolean;
}) {
  return (
    <Paper p="xs" withBorder h="100%">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
          <Text size="sm" fw={500} lineClamp={1}>{title}</Text>
          {subtitle && <Text size="xs" c="dimmed" ff="monospace">{subtitle}</Text>}
          {tags && tags.length > 0 && (
            <Group gap={4} wrap="wrap">
              {tags.map((t) => (
                <Badge key={t} size="xs" variant="outline" color="gray">{t}</Badge>
              ))}
            </Group>
          )}
        </Stack>
        <Group gap={2} wrap="nowrap" style={{ flexShrink: 0 }}>
          {onEdit && (
            <ActionIcon size="sm" variant="subtle" onClick={onEdit}>✏️</ActionIcon>
          )}
          <ActionIcon size="sm" variant="subtle" color="red" loading={isSaving} onClick={onDelete}>🗑</ActionIcon>
        </Group>
      </Group>
    </Paper>
  );
}

// ─── Modals ───────────────────────────────────────────────────────────────────

function CategoryModal({
  opened, onClose, initial, groups, onSave,
}: {
  opened: boolean;
  onClose: () => void;
  initial: CategoryEntry | null;
  groups: { id: string; label_da: string }[];
  onSave: (entry: CategoryEntry) => void;
}) {
  const form = useForm<CategoryEntry>({
    initialValues: initial ?? { id: '', label_da: '', group: groups[0]?.id ?? '', synonyms: [] },
  });
  return (
    <Modal opened={opened} onClose={onClose} title={initial ? 'Rediger kategori' : 'Ny kategori'} centered>
      <form onSubmit={form.onSubmit((v) => { onSave(v); onClose(); })}>
        <Stack gap="sm">
          <TextInput label="ID" placeholder="f.eks. rygsaek" required disabled={!!initial} {...form.getInputProps('id')} />
          <TextInput label="Navn (dansk)" required {...form.getInputProps('label_da')} />
          <Select label="Gruppe" required data={groups.map((g) => ({ label: g.label_da, value: g.id }))} {...form.getInputProps('group')} />
          <TagsInput label="Synonymer" {...form.getInputProps('synonyms')} />
          <Button type="submit">Gem</Button>
        </Stack>
      </form>
    </Modal>
  );
}

function ColorModal({
  opened, onClose, initial, onSave,
}: {
  opened: boolean;
  onClose: () => void;
  initial: ColorEntry | null;
  onSave: (entry: ColorEntry) => void;
}) {
  const form = useForm<ColorEntry>({
    initialValues: initial ?? { id: '', label_da: '', synonyms: [] },
  });
  return (
    <Modal opened={opened} onClose={onClose} title={initial ? 'Rediger farve' : 'Ny farve'} centered>
      <form onSubmit={form.onSubmit((v) => { onSave(v); onClose(); })}>
        <Stack gap="sm">
          <TextInput label="ID" placeholder="f.eks. lyseroed" required disabled={!!initial} {...form.getInputProps('id')} />
          <TextInput label="Navn (dansk)" required {...form.getInputProps('label_da')} />
          <TagsInput label="Synonymer" {...form.getInputProps('synonyms')} />
          <Button type="submit">Gem</Button>
        </Stack>
      </form>
    </Modal>
  );
}

function PatternModal({
  opened, onClose, onSave,
}: {
  opened: boolean;
  onClose: () => void;
  onSave: (entry: { id: string; label_da: string }) => void;
}) {
  const form = useForm({ initialValues: { id: '', label_da: '' } });
  return (
    <Modal opened={opened} onClose={onClose} title="Nyt mønster" centered>
      <form onSubmit={form.onSubmit((v) => { onSave(v); onClose(); form.reset(); })}>
        <Stack gap="sm">
          <TextInput label="ID" placeholder="f.eks. prikket" required {...form.getInputProps('id')} />
          <TextInput label="Navn (dansk)" placeholder="f.eks. Prikket" required {...form.getInputProps('label_da')} />
          <Button type="submit">Gem</Button>
        </Stack>
      </form>
    </Modal>
  );
}

function BrandModal({
  opened, onClose, brandGroups, onSave,
}: {
  opened: boolean;
  onClose: () => void;
  brandGroups: string[];
  onSave: (name: string, group: string) => void;
}) {
  const form = useForm({ initialValues: { name: '', group: brandGroups[0] ?? '' } });
  return (
    <Modal opened={opened} onClose={onClose} title="Nyt mærke" centered>
      <form onSubmit={form.onSubmit((v) => { onSave(v.name, v.group); onClose(); form.reset(); })}>
        <Stack gap="sm">
          <TextInput label="Mærke" placeholder="f.eks. Patagonia" required {...form.getInputProps('name')} />
          <Select label="Gruppe" required data={brandGroups.map((k) => ({ label: k.replace(/_/g, ' '), value: k }))} {...form.getInputProps('group')} />
          <Button type="submit">Gem</Button>
        </Stack>
      </form>
    </Modal>
  );
}

function AttributeModal({
  opened, onClose, onSave,
}: {
  opened: boolean;
  onClose: () => void;
  onSave: (entry: { id: string; label_da: string; synonyms: string[] }) => void;
}) {
  const form = useForm({ initialValues: { id: '', label_da: '', synonyms: [] as string[] } });
  return (
    <Modal opened={opened} onClose={onClose} title="Ny attribut" centered>
      <form onSubmit={form.onSubmit((v) => { onSave(v); onClose(); form.reset(); })}>
        <Stack gap="sm">
          <TextInput label="ID" placeholder="f.eks. slidt" required {...form.getInputProps('id')} />
          <TextInput label="Navn (dansk)" placeholder="f.eks. Slidt" required {...form.getInputProps('label_da')} />
          <TagsInput label="Synonymer" {...form.getInputProps('synonyms')} />
          <Button type="submit">Gem</Button>
        </Stack>
      </form>
    </Modal>
  );
}

// ─── Section wrapper ───────────────────────────────────────────────────────────

function Section({ title, count, onAdd, children }: {
  title: string;
  count?: number;
  onAdd: () => void;
  children: React.ReactNode;
}) {
  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Group gap="xs">
          <Title order={4}>{title}</Title>
          {count !== undefined && <Badge size="sm" variant="light" color="gray">{count}</Badge>}
        </Group>
        <Button size="xs" onClick={onAdd}>+ Tilføj</Button>
      </Group>
      {children}
    </Stack>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

type ModalState<T> = { open: boolean; entry: T | null };

export function Settings() {
  const { data, isLoading, save, isSaving } = useCategories();

  const [catModal, setCatModal] = useState<ModalState<CategoryEntry>>({ open: false, entry: null });
  const [colorModal, setColorModal] = useState<ModalState<ColorEntry>>({ open: false, entry: null });
  const [patternModal, setPatternModal] = useState(false);
  const [brandModal, setBrandModal] = useState(false);
  const [attributeModal, setAttributeModal] = useState(false);

  if (isLoading) return <Center py="xl"><Loader /></Center>;
  if (!data) return null;

  function saveCategory(entry: CategoryEntry) {
    const cats = catModal.entry
      ? data!.categories.map((c) => (c.id === entry.id ? entry : c))
      : [...data!.categories, entry];
    save({ ...data!, categories: cats });
  }

  function saveColor(entry: ColorEntry) {
    const colors = colorModal.entry
      ? data!.colors.map((c) => (c.id === entry.id ? entry : c))
      : [...data!.colors, entry];
    save({ ...data!, colors });
  }

  function addBrand(name: string, group: string) {
    const brands = { ...data!.brands, [group]: [...(data!.brands[group] ?? []), name.trim()] };
    save({ ...data!, brands });
  }

  const brandGroups = Object.keys(data.brands);
  const allBrands = Object.values(data.brands).flat();

  return (
    <div className={pageContainer}>
      <Stack gap="xl">
        <Stack gap={4}>
          <Title order={2}>Kategorier & metadata</Title>
          <Text size="sm" c="dimmed">
            Data gemmes i{' '}
            <Text span ff="monospace" size="sm" c="dimmed">apps/api/src/data/categories.json</Text>.
            Ændringer i matching-engine træder i kraft ved server-genstart.
          </Text>
        </Stack>

        {/* Categories — grouped */}
        <Section title="Kategorier" count={data.categories.length} onAdd={() => setCatModal({ open: true, entry: null })}>
          <Accordion multiple variant="contained" chevronPosition="left">
            {data.category_groups.map((group) => {
              const cats = data.categories.filter((c) => c.group === group.id);
              if (cats.length === 0) return null;
              return (
                <Accordion.Item key={group.id} value={group.id}>
                  <Accordion.Control>
                    <Group gap="xs">
                      <Text size="sm" fw={500}>{group.label_da}</Text>
                      <Badge size="xs" variant="light" color="gray">{cats.length}</Badge>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="xs">
                      {cats.map((cat) => (
                        <MetaCard
                          key={cat.id}
                          title={cat.label_da}
                          subtitle={cat.id}
                          tags={cat.synonyms}
                          isSaving={isSaving}
                          onEdit={() => setCatModal({ open: true, entry: cat })}
                          onDelete={() => save({ ...data!, categories: data!.categories.filter((c) => c.id !== cat.id) })}
                        />
                      ))}
                    </SimpleGrid>
                  </Accordion.Panel>
                </Accordion.Item>
              );
            })}
          </Accordion>
        </Section>

        <Divider />

        {/* Colors */}
        <Section title="Farver" count={data.colors.length} onAdd={() => setColorModal({ open: true, entry: null })}>
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="xs">
            {data.colors.map((color) => (
              <MetaCard
                key={color.id}
                title={color.label_da}
                subtitle={color.id}
                tags={color.synonyms}
                isSaving={isSaving}
                onEdit={() => setColorModal({ open: true, entry: color })}
                onDelete={() => save({ ...data!, colors: data!.colors.filter((c) => c.id !== color.id) })}
              />
            ))}
          </SimpleGrid>
        </Section>

        <Divider />

        {/* Brands — grouped */}
        <Section title="Mærker" count={allBrands.length} onAdd={() => setBrandModal(true)}>
          {Object.entries(data.brands).map(([groupKey, brands]) => (
            <Stack key={groupKey} gap="xs">
              <Text size="xs" tt="uppercase" fw={600} c="dimmed">{groupKey.replace(/_/g, ' ')}</Text>
              <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="xs">
                {brands.map((b) => (
                  <MetaCard
                    key={b}
                    title={b}
                    isSaving={isSaving}
                    onDelete={() => {
                      const updated = { ...data!.brands, [groupKey]: data!.brands[groupKey].filter((x) => x !== b) };
                      save({ ...data!, brands: updated });
                    }}
                  />
                ))}
              </SimpleGrid>
            </Stack>
          ))}
        </Section>

        <Divider />

        {/* Patterns */}
        <Section title="Mønstre" count={data.patterns.length} onAdd={() => setPatternModal(true)}>
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="xs">
            {data.patterns.map((p) => (
              <MetaCard
                key={p.id}
                title={p.label_da}
                subtitle={p.id}
                isSaving={isSaving}
                onDelete={() => save({ ...data!, patterns: data!.patterns.filter((x) => x.id !== p.id) })}
              />
            ))}
          </SimpleGrid>
        </Section>

        <Divider />

        {/* Attributes */}
        <Section title="Attributter" count={data.attributes.length} onAdd={() => setAttributeModal(true)}>
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="xs">
            {data.attributes.map((attr) => (
              <MetaCard
                key={attr.id}
                title={attr.label_da}
                subtitle={attr.id}
                tags={attr.synonyms}
                isSaving={isSaving}
                onDelete={() => save({ ...data!, attributes: data!.attributes.filter((a) => a.id !== attr.id) })}
              />
            ))}
          </SimpleGrid>
        </Section>
      </Stack>

      <CategoryModal
        key={catModal.entry?.id ?? 'new-cat'}
        opened={catModal.open}
        onClose={() => setCatModal({ open: false, entry: null })}
        initial={catModal.entry}
        groups={data.category_groups}
        onSave={saveCategory}
      />
      <ColorModal
        key={colorModal.entry?.id ?? 'new-color'}
        opened={colorModal.open}
        onClose={() => setColorModal({ open: false, entry: null })}
        initial={colorModal.entry}
        onSave={saveColor}
      />
      <PatternModal
        opened={patternModal}
        onClose={() => setPatternModal(false)}
        onSave={(e) => save({ ...data!, patterns: [...data!.patterns, e] })}
      />
      <BrandModal
        opened={brandModal}
        onClose={() => setBrandModal(false)}
        brandGroups={brandGroups}
        onSave={addBrand}
      />
      <AttributeModal
        opened={attributeModal}
        onClose={() => setAttributeModal(false)}
        onSave={(e) => save({ ...data!, attributes: [...data!.attributes, e] })}
      />
    </div>
  );
}
